/**
 * Apple Pay sheet overlay
 *
 * Outside Safari, Apple's JS SDK draws its QR-code sheet inside the payment
 * iframe, which is only as tall as the form. While the iframe reports the sheet
 * as open, the iframe covers the whole viewport so the sheet renders in full;
 * its original styles are restored when the sheet closes.
 *
 * The iframe is restyled in place, never moved or re-created: either would
 * reload it and end the Apple Pay session running inside.
 */

import { Z_INDEX } from '../constants/sdk-constants';

const OVERLAY_STYLES: Record<string, string> = {
  position: 'fixed',
  inset: '0',
  width: '100vw',
  height: '100vh',
  'max-width': 'none',
  'max-height': 'none',
  margin: '0',
  padding: '0',
  border: '0',
  background: 'transparent',
  'z-index': String(Z_INDEX.MODAL),
};

type SavedState = {
  iframe: HTMLIFrameElement;
  iframeStyle: string | null;
  rootOverflow: string;
  usesTopLayer: boolean;
};

export class ApplePaySheetOverlay {
  private saved: SavedState | null = null;

  get isOpen(): boolean {
    return this.saved !== null;
  }

  open(iframe: HTMLIFrameElement): void {
    if (this.saved) return;

    // The top layer renders above every stacking context of the host page
    // (transforms, z-index) without moving the element.
    const usesTopLayer = typeof iframe.showPopover === 'function';

    this.saved = {
      iframe,
      iframeStyle: iframe.getAttribute('style'),
      rootOverflow: document.documentElement.style.overflow,
      usesTopLayer,
    };

    document.documentElement.style.overflow = 'hidden';
    for (const [property, value] of Object.entries(OVERLAY_STYLES)) {
      iframe.style.setProperty(property, value, 'important');
    }

    if (usesTopLayer) {
      try {
        iframe.setAttribute('popover', 'manual');
        iframe.showPopover();
      } catch {
        // Fixed positioning alone still covers the viewport.
        iframe.removeAttribute('popover');
        this.saved.usesTopLayer = false;
      }
    }
  }

  close(): void {
    const saved = this.saved;
    if (!saved) return;
    this.saved = null;

    const { iframe } = saved;
    if (saved.usesTopLayer) {
      try {
        iframe.hidePopover();
      } catch {
        // Already hidden (e.g. the iframe left the document).
      }
      iframe.removeAttribute('popover');
    }

    if (saved.iframeStyle === null) {
      iframe.removeAttribute('style');
    } else {
      iframe.setAttribute('style', saved.iframeStyle);
    }
    document.documentElement.style.overflow = saved.rootOverflow;
  }
}
